import {
    Box,
    Typography,
    Avatar,
    IconButton,
    Button,
    TextField,
    MenuItem,
    Table,
    TableBody,
    TableRow,
    TableCell,
    InputAdornment,
    CircularProgress,
    Switch,
    useMediaQuery,
    useTheme,
    Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import {useEffect, useRef} from "react";
import {useForm, Controller, Control, FieldValues} from "react-hook-form";
import { securitySettingsSteps } from "./components/walkthrough-types";
import { securitySettingsQuizQuestions } from "./components/quiz";
import { useModuleWalkthrough } from "../../common/components/walkthroughV3/useModuleWalkthrough";
import WalkthroughTrigger from "../../common/components/walkthroughV3/walkthrough-trigger";
import useLandingPageStore from "../landing-page/store";
import usePrivacyStore from "../landing-page/components/privacy-statement/api/privacy-store";

import {zodResolver} from "@hookform/resolvers/zod";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import useMainStore from "../landing-page/api/store";
import {initialValues, userFormSchema, UserFormSchema} from "../../common/components/user-invitation-modal/api/schema";
import {commonTextFieldProps, datePickerProps} from "../../common/components/textfield";
import {genderOptions} from "../../common/enums/gender";
import {CivilStatus, civilStatusOptions} from "../../common/enums/civil-status";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {
    useSendEmailOTPCode,
    useSendNumberOTPCode,
    useUpdateEmail,
    useUpdateMobileNumber,
    useUpdatePassword,
    useUpdateTwoFactorEnabled,
    useUpdateUser,
    useValidateEmail,
    useValidateNumber,
} from "./api/hooks";
import useUserStore from "./api/store";
import PasswordConfirmDialog from "./components/confirmation-modal";
import { AuthenticatorSetup } from "./components/authenticator-setup";
import {Cancel, Check, Person, Visibility, VisibilityOff} from "@mui/icons-material";
import {enqueueSnackbar} from "notistack";
import {useLogout} from "../signin-page/api/hooks";
import {Handlers} from "./api/handlers";
import SuccessModal from "./components/success-modal";
import {ContactNumberTextField} from "../../common/components/contact-number-textfield";
import {ImageCropDialog} from "../../common/components/user-inv-modal/components/image-crop-dialog";
import {
    dialogBoxImagePlaceholder,
    dialogBoxPreviewUrl,
    dialogMainBox,
} from "../../common/components/user-inv-modal/sxprops";
import {useGetMyProfile} from "../landing-page/api/hooks";
import {pascalToRegular} from "../../common/enums/user-roles";
import {getHighestRole} from "../../common/utils/role-identifier";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import queryClient from "../../common/query-client";
// import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

const EditProfile: React.FC = () => {
    const {currentUser, setCurrentUser} = useMainStore();
    const { setOpenModal: setNavDrawerOpen, setOpenSecurity } = useLandingPageStore();
    const { openPrivacyModal } = usePrivacyStore();
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const {mutateAsync: validateEmail} = useValidateEmail();
    const {mutate: sendEmailOTP} = useSendEmailOTPCode();
    const {mutateAsync: validateNumber} = useValidateNumber();
    const {mutate: sendNumber} = useSendNumberOTPCode();
    const {mutate: updateMobileNumber} = useUpdateMobileNumber();
    const {mutate: updateEmail} = useUpdateEmail();
    const {mutate: updatePassword, isPending: updatePasswordPending} = useUpdatePassword();
    const {mutate: updateTwoFactorEnabled} = useUpdateTwoFactorEnabled();
    const getMyProfile = useGetMyProfile();

    const isMissingBirthday = !currentUser?.birthDate;
    const isDefaultPassword = currentUser?.isDefaultPassword;

    const logout = useLogout();
    const handleLogout = () => {
        logout.mutate();
    };
    const handlers = new Handlers();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.only("mobile"));

    const {
        control,
        setValue,
        getValues,
        watch,
        setError,
        clearErrors,
        reset,
        formState: {errors},
    } = useForm<UserFormSchema>({
        // mode: 'onBlur',
        resolver: zodResolver(userFormSchema),
        defaultValues: {
            ...initialValues,
            ...(currentUser || {}),
        },
    });

    const [wLastName, wFirstName, wBirthDate] = watch(["lastName", "firstName", "birthDate"]);

    const isRequiredProfileComplete = !!wFirstName?.toString().trim() && !!wLastName?.toString().trim() && !!wBirthDate;

    useEffect(() => {
        if (!currentUser) return;
        setIsEditingEmail(false);
        setIsEditingMobileNumber(false);
        setIsEditingPassword(false);
        reset({
            ...initialValues,
            ...currentUser,
        });
    }, [currentUser, reset]);

    const updateUser = useUpdateUser();
    const {
        previewUrl,
        previewUrl2,
        originalFileName,
        openCropDialog,
        setOpenCropDialog,
        setOriginalFileName,
        setPreviewUrl2,
        setAddModal,
        setCurrentEditContext,
        isEditingEmail,
        setIsEditingEmail,
        isEditingMobileNumber,
        setIsEditingMobileNumber,
        isEditingPassword,
        setIsEditingPassword,
        emailStatus,
        setEmailStatus,
        checkingEmail,
        setCheckingEmail,
        emailOtpSent,
        setEmailOtpSent,
        numberOtpSent,
        setNumberOtpSent,
        numberStatus,
        setNumberStatus,
        checkingNumber,
        setCheckingNumber,
        passwordValid,
        oldPassword,
        showNewPassword,
        showConfirmPassword,
        openModal,
        setOpenModal,
        setPasswordValid,
        setPreviewUrl,
        isEditing,
        setIsEditing,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        emailOTP,
        setEmailOTP,
        phoneNumberOTP,
        setPhoneNumberOTP,
        setShowConfirmPassword,
        setShowNewPassword,
        setIsTwoFactorEnabled,
    } = useUserStore();

    useEffect(() => {
        if (isMissingBirthday) {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [setIsEditing, isMissingBirthday]);

    const onSubmit = (data: UserFormSchema) => {
        if (!currentUser) return;
        setCurrentUser({
            ...currentUser,
            ...data,
            id: currentUser.id,
        });

        updateUser.mutate(data, {
            onSuccess: async () => {
                await getMyProfile.refetch();
                setIsEditing(false);
            },
        });
        setIsEditing(false);
    };

    const { walkthroughUI, handleLightbulbClick } = useModuleWalkthrough({
        steps: securitySettingsSteps,
        questions: securitySettingsQuizQuestions,
        storageKey: "security-settings-walkthrough",
        trackKey: "SecuritySettings",
        isEnabled: !openPrivacyModal,
        onStart: () => {
            setNavDrawerOpen(true);
            setOpenSecurity(true);
        },
        onNext: (_step: any, index: number) => {
            // index 5 = Change Button step → next is Identity Verification: open modal
            if (index === 5) {
                setCurrentEditContext("email");
                setAddModal(true);
            }
            // index 6 = Identity Verification step → next is Change Email: close modal
            if (index === 6) {
                setAddModal(false);
                setCurrentEditContext("");
            }
        },
        onPrev: (_step: any, index: number) => {
            // index 7 = Change Email → going back to Identity Verification: re-open modal
            if (index === 7) {
                setCurrentEditContext("email");
                setAddModal(true);
            }
            // index 6 = Identity Verification → going back to Change Button: close modal
            if (index === 6) {
                setAddModal(false);
                setCurrentEditContext("");
            }
        },
        onCloseAll: () => {
            setAddModal(false);
            setCurrentEditContext("");
        },
        quizTitle: "Security Settings Knowledge Check",
        moduleName: "Security Settings",
    });

    if (!currentUser) return null;

    const handleAddClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        const file = files[0];
        if (file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png") {
            const url = URL.createObjectURL(file);
            setPreviewUrl2(url);
            setOriginalFileName(file.name);
            setOpenCropDialog(true);
            event.target.value = "";
        } else {
            console.warn("Only JPG, JPEG, or PNG files are allowed.");
        }
    };

    const passwordValidationMessages = [
        {
            label: "Password must be a minimum of 8 characters.",
            valid: passwordValid.minLength,
        },
        {
            label: "Password must include one uppercase letter (A-Z).",
            valid: passwordValid.hasUppercase,
        },
        {
            label: "Password must include one lowercase letter (a-z).",
            valid: passwordValid.hasLowercase,
        },
        {
            label: "Password must include one special character (e.g., !, @, #, $,).",
            valid: passwordValid.hasSpecialChar,
        },
        {
            label: "Password must include one number (0-9).",
            valid: passwordValid.hasNumber,
        },
        {label: "Passwords must match", valid: passwordValid.passwordsMatch},
    ];

    const handleEmailOTP = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setEmailOTP(value);
    };

    const handleChangePhoneNumberOTP = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPhoneNumberOTP(value);
    };

    const handleContinue = () => {
        setOpenModal(false);
        setTimeout(() => {
            handleLogout();
        }, 3000);
    };

    const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>, newChecked: boolean) => {
        setIsTwoFactorEnabled(newChecked);

        const payload = {
            twoFactorEnabled: newChecked,
        };

        updateTwoFactorEnabled(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["api/privileges"],
                });
                setCurrentUser({
                    ...currentUser,
                    twoFactorEnabled: newChecked,
                    id: currentUser.id,
                });
                enqueueSnackbar("2FA updated successfully!", {
                    variant: "success",
                });
            },
            onError: (err) => {
                console.error("Failed", err);
            },
        });
    };

    const dpProps = datePickerProps(errors, isMissingBirthday);

    const missingFields: string[] = [];

    if (!wFirstName?.trim()) missingFields.push("First Name");
    if (!wLastName?.trim()) missingFields.push("Last Name");
    if (!wBirthDate) missingFields.push("Birth Date");

    const tooltipMessage =
        missingFields.length > 0 ? "Fill up Personal Information to proceed on Login Information details" : "";

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box display="flex" alignItems="center">
                    <Typography
                        sx={{
                            fontWeight: 400,
                            color: "primary.main",
                            mb: 5,
                            fontSize: isMobile ? "20px" : "24px",
                            pl: 2,
                            pt: 2,
                        }}
                    >
                        Edit Profile
                    </Typography>
                    </Box>
                    <WalkthroughTrigger id="security-settings-walkthrough-start-button" onClick={handleLightbulbClick} />
                    {(isMissingBirthday || isDefaultPassword) && (
                        <Box display={"flex"} alignItems={"center"} pl={2} mb={5} gap={1}>
                            <ErrorOutlineIcon sx={{fontSize: "48px", color: "#FFC000"}} />
                            <Typography
                                sx={{
                                    color: "primary.main",
                                    fontSize: "14px",
                                    textAlign: "left",
                                }}
                            >
                                Complete Your Profile for Better Security & Personalization.
                                <br />
                                For your protection and to access all features, please update your password and enter
                                your birthday.
                                <br />
                                This helps us verify your identity and tailor your experience.
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Profile Picture and Name */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: "center",
                        justifyContent: isMobile ? "center" : "flex-start",
                        gap: 2,
                        mb: 4,
                        pl: isMobile ? 0 : 2,
                        textAlign: isMobile ? "center" : "left",
                    }}
                >
                    <Box sx={{position: "relative", width: "120px", height: "120px"}}>
                        <Box sx={dialogMainBox}>
                            <Box sx={dialogBoxImagePlaceholder}>
                                {previewUrl ? (
                                    <Box component="img" src={previewUrl} alt="Profile" sx={dialogBoxPreviewUrl} />
                                ) : currentUser?.photoUrl ? (
                                    <Avatar
                                        src={currentUser.photoUrl}
                                        alt={currentUser.firstName}
                                        sx={{width: "120px", height: "120px"}}
                                    />
                                ) : (
                                    <Person style={{ width: 100, height: 100, fill: "white" }} />
                                )}
                            </Box>
                            <Box>
                                <IconButton
                                    id="edit-profile-picture-button"
                                    data-testid="edit-profile-picture-button"
                                    onClick={handleAddClick}
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        right: 0,
                                        backgroundColor: "#f44336",
                                        color: "white",
                                        width: 36,
                                        height: 36,
                                        border: "2px solid white",
                                        "&:hover": {backgroundColor: "#d32f2f"},
                                    }}
                                    size="small"
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                data-testid="edit-profile-picture-file-input"
                                style={{display: "none"}}
                                accept="image/jpeg,image/jpg,image/png"
                                onChange={handleFileChange}
                            />
                        </Box>
                    </Box>

                    <Box sx={{textAlign: "left"}}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 500,
                                color: "#123257",
                                fontSize: isMobile ? "20px" : "24px",
                            }}
                        >
                            {currentUser.firstName || currentUser.lastName
                                ? `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim()
                                : "N/A"}
                        </Typography>

                        <Typography
                            sx={{fontWeight: 400, color: "#6D87AA", fontSize: "16px"}}
                            textAlign={isMobile ? "center" : "left"} // ✅ Add this line
                        >
                            {pascalToRegular(getHighestRole(currentUser.roles)) || "N/A"}
                        </Typography>
                    </Box>
                </Box>

                {/* Personal Information */}
                <Box id="edit-profile-personal-section">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 500,
                            color: "#123257",
                            fontSize: isMobile ? "16px" : "20px",
                            pl: 2,
                        }}
                    >
                        Personal Information
                    </Typography>
                    <Box sx={{display: "flex", gap: 1}}>
                        {isEditing && (
                            <Button
                                id="edit-profile-personal-cancel-button"
                                data-testid="edit-profile-personal-cancel-button"
                                onClick={() => {
                                    reset({
                                        ...initialValues,
                                        ...currentUser,
                                        birthDate: currentUser?.birthDate
                                            ? dayjs(currentUser.birthDate).format("YYYY-MM-DD")
                                            : "",
                                    });

                                    setIsEditing(false);
                                }}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: "24px",
                                    px: 3,
                                    color: "#123257",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                }}
                                disabled={updateUser.isPending}
                            >
                                Cancel
                            </Button>
                        )}

                        <Button
                            disabled={updateUser.isPending}
                            id="edit-save-profile-button"
                            data-testid="edit-save-profile-button"
                            startIcon={<EditOutlinedIcon />}
                            variant="outlined"
                            onClick={() => {
                                if (!isEditing) {
                                    setIsEditing(true);
                                    return;
                                }

                                const data = getValues();

                                clearErrors(["firstName", "lastName", "birthDate", "gender", "civilStatus"]);

                                let hasError = false;

                                if (!data.firstName?.trim()) {
                                    setError("firstName", {
                                        type: "manual",
                                        message: "Required",
                                    });
                                    hasError = true;
                                }

                                if (!data.lastName?.trim()) {
                                    setError("lastName", {
                                        type: "manual",
                                        message: "Required",
                                    });
                                    hasError = true;
                                }

                                if (!data.birthDate) {
                                    setError("birthDate", {type: "manual", message: "Select Birthday"});
                                    return;
                                }

                                const d = dayjs(data.birthDate);

                                // console.log(raw);

                                if (!d.isValid()) {
                                    setError("birthDate", {type: "manual", message: "Invalid date format"});
                                    return;
                                }

                                if (!d.isBefore(dayjs(), "day")) {
                                    setError("birthDate", {
                                        type: "manual",
                                        message: "Birthdate cannot be today or in the future",
                                    });
                                    return;
                                }

                                if (d.isBefore(dayjs("1900-01-01"), "day")) {
                                    setError("birthDate", {
                                        type: "manual",
                                        message: "Oops! Birthdate seems too old. Cannot be earlier than 01/01/1900.",
                                    });
                                    return;
                                }
                                if (hasError) {
                                    return;
                                }

                                onSubmit(data);
                            }}
                            sx={{
                                textTransform: "none",
                                borderRadius: "24px",
                                px: 3,
                                color: "#123257",
                                borderColor: "#123257",
                                fontSize: "14px",
                                fontWeight: 500,
                            }}
                        >
                            {isEditing ? "Save" : "Edit"}
                        </Button>
                    </Box>
                </Box>

                {/* Info Fields */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        justifyContent: "flex-start",
                        paddingLeft: isEditing ? 2 : 0,
                    }}
                >
                    {isEditing ? (
                        <>
                            {isMobile ? (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 3,
                                        width: "100%",
                                        textAlign: "left",
                                        pl: 1,
                                    }}
                                >
                                    <Controller
                                        name="prefix"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                id="prefix-profile-textfield"
                                                {...field}
                                                fullWidth
                                                label="Prefix (Optional)"
                                                sx={commonTextFieldProps}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="firstName"
                                        rules={{required: "Required"}}
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="First Name"
                                                error={!!errors.firstName}
                                                helperText={errors.firstName?.message}
                                                sx={commonTextFieldProps}
                                                inputProps={{ "data-testid": "first-name-profile-textfield" }}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    if (errors.firstName) {
                                                        clearErrors("firstName");
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="middleName"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Middle Name (Optional)"
                                                sx={commonTextFieldProps}
                                                inputProps={{ "data-testid": "middle-name-profile-textfield" }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="lastName"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Last Name"
                                                error={!!errors.lastName}
                                                helperText={errors.lastName?.message}
                                                sx={commonTextFieldProps}
                                                inputProps={{ "data-testid": "last-name-profile-textfield" }}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    if (errors.lastName) {
                                                        clearErrors("lastName");
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="suffix"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Suffix (Optional)"
                                                sx={commonTextFieldProps}
                                                inputProps={{ "data-testid": "suffix-profile-textfield" }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="birthDate"
                                        control={control}
                                        render={({field}) => (
                                            <DatePicker
                                                label="Birth Date"
                                                maxDate={dayjs().subtract(1, "day")}
                                                minDate={dayjs("1900-01-01")}
                                                value={field.value ? dayjs(field.value) : null}
                                                onChange={(date) => {
                                                    if (date === null) {
                                                        field.onChange("");
                                                        return;
                                                    }
                                                    const d = dayjs(date);
                                                    if (d.isValid()) {
                                                        field.onChange(d.format("YYYY-MM-DD"));
                                                        if (errors.birthDate) clearErrors("birthDate");
                                                    }
                                                }}
                                                {...dpProps}
                                                slotProps={{
                                                    ...dpProps?.slotProps,
                                                    textField: {
                                                        ...dpProps?.slotProps?.textField,
                                                        required: true,
                                                        sx: {
                                                            ...commonTextFieldProps,
                                                        },
                                                        inputProps: { "data-testid": "birth-date-profile-textfield" },
                                                    },
                                                }}
                                            />
                                        )}
                                    />{" "}
                                    <Controller
                                        name="gender"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                required
                                                label="Gender"
                                                select
                                                sx={commonTextFieldProps}
                                                error={!!errors.gender}
                                                helperText={errors.gender?.message}
                                                slotProps={{ select: { SelectDisplayProps: { "data-testid": "gender-profile-textfield" } as any } }}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                    if (errors.gender) {
                                                        clearErrors("gender");
                                                    }
                                                }}
                                            >
                                                {genderOptions.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                    <Controller
                                        name="civilStatus"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                required
                                                label="Civil Status"
                                                select
                                                sx={commonTextFieldProps}
                                                error={!!errors.civilStatus}
                                                helperText={errors.civilStatus?.message}
                                                slotProps={{ select: { SelectDisplayProps: { "data-testid": "civil-status-profile-textfield" } as any } }}
                                                onChange={(e) => {
                                                    field.onChange(e);

                                                    if (errors.civilStatus) {
                                                        clearErrors("civilStatus");
                                                    }
                                                }}
                                            >
                                                {civilStatusOptions.map((opt) => (
                                                    <MenuItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />{" "}
                                    <Controller
                                        name="customName"
                                        control={control}
                                        render={({field}) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Custom Name (Optional)"
                                                sx={commonTextFieldProps}
                                                inputProps={{ "data-testid": "custom-name-profile-textfield" }}
                                            />
                                        )}
                                    />
                                </Box>
                            ) : (
                                <Table
                                    sx={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        "& .MuiTableCell-root": {
                                            border: "none",
                                            paddingBottom: "24px",
                                            width: "33.5%",
                                            verticalAlign: "top",
                                            paddingLeft: "0px",
                                            paddingRight: 1.5,
                                        },
                                    }}
                                >
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                <Controller
                                                    name="lastName"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            label="Last Name"
                                                            error={!!errors.lastName}
                                                            helperText={errors.lastName?.message}
                                                            sx={commonTextFieldProps}
                                                            inputProps={{ "data-testid": "last-name-profile-textfield" }}
                                                            onChange={(e) => {
                                                                field.onChange(e);
                                                                if (errors.lastName) {
                                                                    clearErrors("lastName");
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name="firstName"
                                                    rules={{required: "Required"}}
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            label="First Name"
                                                            error={!!errors.firstName}
                                                            helperText={errors.firstName?.message}
                                                            sx={commonTextFieldProps}
                                                            inputProps={{ "data-testid": "first-name-profile-textfield" }}
                                                            onChange={(e) => {
                                                                field.onChange(e);
                                                                if (errors.firstName) {
                                                                    clearErrors("firstName");
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name="middleName"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Middle Name (Optional)"
                                                            sx={commonTextFieldProps}
                                                            inputProps={{ "data-testid": "middle-name-profile-textfield" }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Controller
                                                    name="suffix"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Suffix (Optional)"
                                                            sx={commonTextFieldProps}
                                                            inputProps={{ "data-testid": "suffix-profile-textfield" }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name="prefix"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Prefix (Optional)"
                                                            sx={commonTextFieldProps}
                                                            inputProps={{ "data-testid": "prefix-profile-textfield" }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name="customName"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            label="Custom Name (Optional)"
                                                            sx={commonTextFieldProps}
                                                            inputProps={{ "data-testid": "custom-name-profile-textfield" }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Controller
                                                    name="birthDate"
                                                    control={control}
                                                    render={({field}) => (
                                                        <DatePicker
                                                            label="Birth Date"
                                                            maxDate={dayjs().subtract(1, "day")}
                                                            minDate={dayjs("1900-01-01")}
                                                            value={field.value ? dayjs(field.value) : null}
                                                            onChange={(date) => {
                                                                if (date === null) {
                                                                    field.onChange("");
                                                                    return;
                                                                }
                                                                const d = dayjs(date);
                                                                if (d.isValid()) {
                                                                    field.onChange(d.format("YYYY-MM-DD"));
                                                                    if (errors.birthDate) clearErrors("birthDate");
                                                                }
                                                            }}
                                                            {...dpProps}
                                                            slotProps={{
                                                                ...dpProps?.slotProps,
                                                                textField: {
                                                                    ...dpProps?.slotProps?.textField,
                                                                    required: true,
                                                                    sx: {
                                                                        ...commonTextFieldProps,
                                                                    },
                                                                    inputProps: { "data-testid": "birth-date-profile-textfield" },
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />{" "}
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name="gender"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            label="Gender"
                                                            select
                                                            sx={commonTextFieldProps}
                                                            error={!!errors.gender}
                                                            helperText={errors.gender?.message}
                                                            slotProps={{ select: { SelectDisplayProps: { "data-testid": "gender-profile-textfield" } as any } }}
                                                            onChange={(e) => {
                                                                field.onChange(e);
                                                                if (errors.gender) {
                                                                    clearErrors("gender");
                                                                }
                                                            }}
                                                        >
                                                            {genderOptions.map((opt) => (
                                                                <MenuItem key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />{" "}
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name="civilStatus"
                                                    control={control}
                                                    render={({field}) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            label="Civil Status"
                                                            select
                                                            sx={commonTextFieldProps}
                                                            error={!!errors.civilStatus}
                                                            helperText={errors.civilStatus?.message}
                                                            slotProps={{ select: { SelectDisplayProps: { "data-testid": "civil-status-profile-textfield" } as any } }}
                                                            onChange={(e) => {
                                                                field.onChange(e);

                                                                if (errors.civilStatus) {
                                                                    clearErrors("civilStatus");
                                                                }
                                                            }}
                                                        >
                                                            {civilStatusOptions.map((opt) => (
                                                                <MenuItem key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    )}
                                                />{" "}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </>
                    ) : (
                        <>
                            {isMobile ? (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 3,
                                        width: "100%",
                                        textAlign: "left",
                                        pl: 2,
                                    }}
                                >
                                    <Field label="Prefix" value={currentUser.prefix || "N/A"} required />
                                    <Field label="First Name" value={currentUser.firstName || "N/A"} required />
                                    <Field label="Middle Name" value={currentUser.middleName || "N/A"} />
                                    <Field label="Last Name" value={currentUser.lastName || "N/A"} />
                                    <Field label="Suffix" value={currentUser.suffix || "N/A"} />
                                    <Field
                                        label="Birthdate"
                                        value={
                                            currentUser.birthDate
                                                ? dayjs(currentUser.birthDate).format("MM/DD/YYYY")
                                                : "N/A"
                                        }
                                        required
                                    />
                                    <Field
                                        label="Gender"
                                        value={getLabelFromOptions(genderOptions, currentUser.gender)}
                                        required
                                    />
                                    <Field
                                        label="Civil Status"
                                        value={getCivilStatusLabel(currentUser.civilStatus)}
                                        required
                                    />
                                    <Field label="Custom Name" value={currentUser.customName || "N/A"} />
                                </Box>
                            ) : (
                                <Table
                                    sx={{
                                        width: "100%",
                                        borderCollapse: "collapse",
                                        "& .MuiTableCell-root": {
                                            border: "none",
                                            paddingBottom: "24px",
                                            width: "33%",
                                            verticalAlign: "top",
                                            textAlign: "left",
                                        },
                                    }}
                                >
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                <Field
                                                    label="Last Name"
                                                    value={currentUser.lastName || "N/A"}
                                                    required
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Field
                                                    label="First Name"
                                                    value={currentUser.firstName || "N/A"}
                                                    required
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Field label="Middle Name" value={currentUser.middleName || "N/A"} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Field label="Suffix" value={currentUser.suffix || "N/A"} />
                                            </TableCell>
                                            <TableCell>
                                                <Field label="Prefix" value={currentUser.prefix || "N/A"} />
                                            </TableCell>
                                            <TableCell>
                                                <Field label="Custom Name" value={currentUser.customName || "N/A"} />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <Field
                                                    label="Birthdate"
                                                    value={
                                                        currentUser.birthDate
                                                            ? dayjs(currentUser.birthDate).format("MM/DD/YYYY")
                                                            : "N/A"
                                                    }
                                                    required
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Field
                                                    label="Gender"
                                                    value={getLabelFromOptions(genderOptions, currentUser.gender)}
                                                    required
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Field
                                                    label="Civil Status"
                                                    value={getCivilStatusLabel(currentUser.civilStatus)}
                                                    required
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </>
                    )}
                </Box>
                </Box>{/* end edit-profile-personal-section */}

                {/* Login Information */}
                <Box id="edit-profile-login-section">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 5,
                        mt: 5,
                        pl: 2,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 500,
                            color: "#123257",
                            fontSize: isMobile ? "16px" : "20px",
                        }}
                    >
                        Login Information
                    </Typography>
                </Box>

                {/* Email Address Section*/}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 5,
                        pl: 2,
                    }}
                >
                    <Box sx={{textAlign: "left", flexGrow: 1}}>
                        {!isEditingEmail && (
                            <Typography fontSize="14px" fontWeight={400} color="#60768E">
                                Email Address{" "}
                            </Typography>
                        )}

                        {isEditingEmail && !emailOtpSent ? (
                            <Box sx={{pr: 2}}>
                                <Controller
                                    name="newEmail"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            id="new-email"
                                            data-testid="new-email"
                                            {...field}
                                            fullWidth
                                            label="New Email"
                                            error={!!errors.newEmail}
                                            helperText={errors.newEmail?.message}
                                            sx={commonTextFieldProps}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                field.onChange(e);
                                                if (!value || !/\S+@\S+\.\S+/.test(value)) {
                                                    setEmailStatus(null);
                                                    return;
                                                }
                                                if (debounceRef.current) clearTimeout(debounceRef.current);
                                                debounceRef.current = setTimeout(async () => {
                                                    setCheckingEmail(true);
                                                    try {
                                                        const isAvailable = await validateEmail({
                                                            newEmail: value,
                                                        });
                                                        setEmailStatus(isAvailable ? "valid" : "invalid");
                                                    } catch (err) {
                                                        console.error("Email validation failed", err);
                                                        setEmailStatus(null);
                                                    } finally {
                                                        setCheckingEmail(false);
                                                    }
                                                }, 500);
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                height: "100%",
                                                                pt: 1.5,
                                                            }}
                                                        >
                                                            {checkingEmail ? (
                                                                <CircularProgress size={20} />
                                                            ) : emailStatus === "valid" ? (
                                                                <Check color="success" />
                                                            ) : emailStatus === "invalid" ? (
                                                                <Cancel color="error" />
                                                            ) : null}
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        ) : isEditingEmail && emailOtpSent ? (
                            <Box sx={{pr: 2}}>
                                <Controller
                                    name="emailOTP"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            id="email-otp"
                                            data-testid="email-otp"
                                            value={emailOTP}
                                            label="Input OTP"
                                            sx={commonTextFieldProps}
                                            onChange={handleEmailOTP}
                                        />
                                    )}
                                />
                            </Box>
                        ) : (
                            <Typography fontWeight={500} fontSize={16}>
                                {currentUser.email}
                            </Typography>
                        )}
                    </Box>

                    <Button
                        id="edit-email-profile-button"
                        data-testid="edit-email-profile-button"
                        variant="outlined"
                        onClick={() => {
                            if (!isEditingEmail) {
                                setCurrentEditContext("email");
                                handlers.handleOpenConfirmationPasswordModal();
                            } else if (!emailOtpSent) {
                                if (emailStatus === "valid") {
                                    sendEmailOTP(
                                        {
                                            currentEmail: currentUser.email,
                                            newEmail: watch("newEmail") ?? "",
                                        },
                                        {
                                            onSuccess: () => {
                                                enqueueSnackbar("OTP sent to new email", {
                                                    variant: "success",
                                                });
                                                setEmailOtpSent(true);
                                            },
                                            onError: () => {
                                                enqueueSnackbar("Failed to send OTP", {
                                                    variant: "error",
                                                });
                                            },
                                        }
                                    );
                                }
                            } else {
                                const payload = {
                                    email: currentUser.email,
                                    otpCode: emailOTP ?? "",
                                };

                                updateEmail(payload, {
                                    onSuccess: () => {
                                        enqueueSnackbar("Email updated successfully!", {
                                            variant: "success",
                                        });
                                        setIsEditingEmail(false);
                                        currentUser.email = watch("newEmail") ?? "";
                                        setEmailOtpSent(false);
                                        setAddModal(false);

                                        setOpenModal(true);
                                    },
                                    onError: (err) => {
                                        console.error("Failed", err);
                                    },
                                });
                            }
                        }}
                        disabled={
                            isEditingEmail &&
                            ((!emailOtpSent && (emailStatus !== "valid" || checkingEmail)) ||
                                (emailOtpSent && !watch("newEmail")))
                        }
                        sx={{
                            textTransform: "none",
                            borderRadius: "24px",
                            px: 2,
                            color: "#123257",
                            borderColor: "#123257",
                            fontSize: "16px",
                            fontWeight: 500,
                        }}
                    >
                        {!isEditingEmail ? "Change" : emailOtpSent ? "Update Email" : "Send Code"}
                    </Button>
                </Box>

                {/* Mobile Number Section*/}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 5,
                        pl: 2,
                    }}
                >
                    <Box sx={{textAlign: "left", flexGrow: 1}}>
                        {!isEditingMobileNumber && (
                            <Typography fontSize="14px" fontWeight={400} color="#60768E">
                                {" "}
                                Mobile Number
                            </Typography>
                        )}
                        {isEditingMobileNumber && !numberOtpSent ? (
                            <Box sx={{pr: 2}}>
                                <Controller
                                    name="newPhoneNumber"
                                    control={control}
                                    render={({field}) => (
                                        <ContactNumberTextField
                                            control={control as unknown as Control<FieldValues>}
                                            id="new-phone-number"
                                            name="newPhoneNumber"
                                            label="Mobile Number"
                                            fullWidth
                                            type="text"
                                            maxLength={10}
                                            onChange={(e) => {
                                                let value = e ?? "";
                                                field.onChange(value);

                                                if (value.length < 10) {
                                                    setNumberStatus("invalid");
                                                } else {
                                                    if (debounceRef.current) clearTimeout(debounceRef.current);
                                                    debounceRef.current = setTimeout(async () => {
                                                        setCheckingNumber(true);
                                                        try {
                                                            const isAvailable = await validateNumber({
                                                                newPhoneNumber: value,
                                                            });
                                                            setNumberStatus(isAvailable ? "valid" : "invalid");
                                                        } catch (err) {
                                                            console.error("Phone Number validation failed", err);
                                                            setNumberStatus(null);
                                                        } finally {
                                                            setCheckingNumber(false);
                                                        }
                                                    }, 500);
                                                }
                                                value = "+63" + value.replace(/^\+63/, "");
                                            }}
                                            slotProps={{
                                                startAdornment: (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            height: "100%",
                                                            pt: 1.5,
                                                        }}
                                                    >
                                                        <InputAdornment position="start">+63</InputAdornment>
                                                    </Box>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                height: "100%",
                                                                pt: 1.5,
                                                            }}
                                                        >
                                                            {checkingNumber ? (
                                                                <CircularProgress size={20} />
                                                            ) : numberStatus === "valid" ? (
                                                                <Check color="success" />
                                                            ) : numberStatus === "invalid" ? (
                                                                <Cancel color="error" />
                                                            ) : null}
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                                inputProps: {
                                                    maxLength: 10,
                                                    inputMode: "numeric",
                                                    pattern: "[0-9]*",
                                                    "data-testid": "new-phone-number",
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        ) : isEditingMobileNumber && numberOtpSent ? (
                            <Box sx={{pr: 2}}>
                                <Controller
                                    name="phoneNumberOTP"
                                    control={control}
                                    render={({field}) => (
                                        <TextField
                                            id="phone-number-otp"
                                            data-testid="phone-number-otp"
                                            {...field}
                                            value={phoneNumberOTP}
                                            onChange={handleChangePhoneNumberOTP}
                                            fullWidth
                                            label="Input OTP"
                                            sx={commonTextFieldProps}
                                        />
                                    )}
                                />
                            </Box>
                        ) : (
                            <Typography fontWeight={500} fontSize={16}>
                                {currentUser.phoneNumber !== undefined ? `+63${currentUser.phoneNumber}` : "N/A"}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        id="edit-mobile-profile-button"
                        data-testid="edit-mobile-profile-button"
                        variant="outlined"
                        onClick={() => {
                            if (!isEditingMobileNumber) {
                                setCurrentEditContext("mobileNumber");
                                handlers.handleOpenConfirmationPasswordModal();
                            } else if (!numberOtpSent) {
                                if (numberStatus === "valid") {
                                    sendNumber(
                                        {
                                            currentEmail: currentUser.email,
                                            newPhoneNumber: watch("newPhoneNumber") ?? "",
                                        },
                                        {
                                            onSuccess: () => {
                                                enqueueSnackbar("OTP sent to email", {
                                                    variant: "success",
                                                });
                                                setNumberOtpSent(true);
                                            },
                                            onError: () => {
                                                enqueueSnackbar("Failed to send OTP", {
                                                    variant: "error",
                                                });
                                            },
                                        }
                                    );
                                }
                            } else {
                                const payload = {
                                    email: currentUser.email,
                                    otpCode: phoneNumberOTP ?? "",
                                };

                                updateMobileNumber(payload, {
                                    onSuccess: () => {
                                        enqueueSnackbar("Mobile number updated successfully!", {
                                            variant: "success",
                                        });
                                        setIsEditingMobileNumber(false);
                                        setAddModal(false);
                                        setPhoneNumberOTP("");
                                        setNumberOtpSent(false);
                                        currentUser.phoneNumber = watch("newPhoneNumber");
                                        queryClient.invalidateQueries({
                                            queryKey: ["api/privileges"],
                                        });
                                    },
                                    onError: (err) => {
                                        console.error("Failed", err);
                                    },
                                });
                            }
                        }}
                        disabled={
                            isEditingMobileNumber &&
                            ((!numberOtpSent && (numberStatus !== "valid" || checkingNumber)) ||
                                (numberOtpSent && !watch("newPhoneNumber")))
                        }
                        sx={{
                            textTransform: "none",
                            borderRadius: "24px",
                            px: 2,
                            color: "#123257",
                            borderColor: "#123257",
                            fontSize: "16px",
                            fontWeight: 500,
                        }}
                    >
                        {!isEditingMobileNumber ? "Change" : numberOtpSent ? "Update Mobile Number" : "Send Code"}
                    </Button>
                </Box>

                {/* Password Section*/}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        pl: 2,
                    }}
                >
                    <Box sx={{textAlign: "left", flexGrow: 1}}>
                        {!isEditingPassword && (
                            <Box display={"flex"} alignItems={"center"} gap={5}>
                                <Typography fontSize="14px" fontWeight={400} color="#60768E">
                                    Password{" "}
                                </Typography>
                                {isDefaultPassword && (
                                    <Typography fontSize="14px" fontWeight={400} color="error">
                                        Please Change your default Password!
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {isEditingPassword ? (
                            <Box sx={{display: "flex", flexDirection: "column", gap: 2, pb: 5}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                    <Controller
                                        name="newPassword"
                                        control={control}
                                        render={({field, fieldState: {error}}) => (
                                            <TextField
                                                {...field}
                                                id="new-password"
                                                data-testid="new-password"
                                                value={newPassword}
                                                label="Create New Password"
                                                fullWidth
                                                sx={commonTextFieldProps}
                                                onChange={(e) => handlers.handlePasswordChange(e, confirmPassword)}
                                                type={showNewPassword ? "text" : "password"}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={{alignItems: "center"}}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        height: "100%",
                                                                        pt: 1.5,
                                                                    }}
                                                                >
                                                                    <IconButton
                                                                        id="new-password-toggle-visibility-button"
                                                                        data-testid="new-password-toggle-visibility-button"
                                                                        aria-label="toggle password visibility"
                                                                        onClick={handlers.handleClickShowPassword}
                                                                        onMouseDown={handlers.handleMouseDownPassword}
                                                                        edge="end"
                                                                        sx={{color: "#f44336"}}
                                                                        size="small"
                                                                    >
                                                                        {showNewPassword ? (
                                                                            <Visibility />
                                                                        ) : (
                                                                            <VisibilityOff />
                                                                        )}
                                                                    </IconButton>
                                                                </Box>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="confirmPassword"
                                        control={control}
                                        render={({field, fieldState: {error}}) => (
                                            <TextField
                                                {...field}
                                                id="confirm-password"
                                                data-testid="confirm-password"
                                                sx={commonTextFieldProps}
                                                onChange={(e) => handlers.handleConfirmPasswordChange(e, newPassword)}
                                                value={confirmPassword}
                                                label="Confirm Password"
                                                fullWidth
                                                type={showConfirmPassword ? "text" : "password"}
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end" sx={{alignItems: "center"}}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        height: "100%",
                                                                        pt: 1.5,
                                                                    }}
                                                                >
                                                                    <IconButton
                                                                        id="confirm-password-toggle-visibility-button"
                                                                        data-testid="confirm-password-toggle-visibility-button"
                                                                        aria-label="toggle password visibility"
                                                                        onClick={
                                                                            handlers.handleClickShowConfirmPassword
                                                                        }
                                                                        onMouseDown={handlers.handleMouseDownPassword}
                                                                        edge="end"
                                                                        sx={{color: "#f44336"}}
                                                                        size="small"
                                                                    >
                                                                        {showConfirmPassword ? (
                                                                            <Visibility />
                                                                        ) : (
                                                                            <VisibilityOff />
                                                                        )}
                                                                    </IconButton>
                                                                </Box>
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                                <Box sx={{display: "flex", flexDirection: "row", gap: 2}}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "50%",
                                            gap: 1,
                                        }}
                                    >
                                        {passwordValidationMessages
                                        .filter((validation) => validation.label !== "Passwords must match")
                                        .map((validation, index) => (
                                            <Box key={index} sx={{display: "flex", alignItems: "center"}}>
                                                {validation.valid ? (
                                                    <Check sx={{color: "#1CA568"}} />
                                                ) : (
                                                    <Cancel sx={{color: "#F93B4C"}} />
                                                )}
                                                <Box sx={{marginLeft: 1}}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            color: validation.valid ? "#1CA568" : "#F93B4C",
                                                        }}
                                                    >
                                                        {validation.label}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "50%",
                                            gap: 1,
                                        }}
                                    >
                                        {passwordValidationMessages
                                        .filter((validation) => validation.label === "Passwords must match")
                                        .map((validation, index) => (
                                            <Box key={index} sx={{display: "flex", alignItems: "center"}}>
                                                {validation.valid ? (
                                                    <Check sx={{color: "#1CA568"}} />
                                                ) : (
                                                    <Cancel sx={{color: "#F93B4C"}} />
                                                )}
                                                <Box sx={{marginLeft: 1}}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            color: validation.valid ? "#1CA568" : "#F93B4C",
                                                        }}
                                                    >
                                                        {validation.label}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Typography fontWeight={500} fontSize={16}>
                                **********
                            </Typography>
                        )}{" "}
                    </Box>
                    {!isEditingPassword && (
                        <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                            <Tooltip title={!isRequiredProfileComplete ? tooltipMessage : ""} arrow placement="top">
                                <span>
                                    <Button
                                        id="edit-password-profile-button"
                                        data-testid="edit-password-profile-button"
                                        variant="outlined"
                                        disabled={!isRequiredProfileComplete || isEditing}
                                        onClick={() => {
                                            setCurrentEditContext("password");
                                            handlers.handleOpenConfirmationPasswordModal();
                                        }}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "24px",
                                            px: 2,
                                            color: "#123257",
                                            borderColor: "#123257",
                                            fontSize: "16px",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Change
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
                {isEditingPassword && (
                    <Box sx={{display: "flex", gap: 2, pb: 3, justifyContent: "flex-end"}}>
                        <Button
                            onClick={() => {
                                setIsEditingPassword(false);
                                setNewPassword("");
                                setConfirmPassword("");
                                setPasswordValid({
                                    minLength: false,
                                    hasUppercase: false,
                                    hasLowercase: false,
                                    hasSpecialChar: false,
                                    hasNumber: false,
                                    passwordsMatch: false,
                                });
                            }}
                            sx={{
                                textTransform: "none",
                                borderRadius: "24px",
                                px: 2,
                                color: "#123257",
                                fontSize: "16px",
                                fontWeight: 500,
                            }}
                            disabled={updatePasswordPending}
                        >
                            Cancel
                        </Button>
                        <Tooltip
                            title={
                                isEditing
                                    ? "You have unsaved changes. Please save your Personal Information first."
                                    : ""
                            }
                            arrow
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        const allValid =
                                            passwordValid.minLength &&
                                            passwordValid.hasUppercase &&
                                            passwordValid.hasLowercase &&
                                            passwordValid.hasSpecialChar &&
                                            passwordValid.hasNumber &&
                                            passwordValid.passwordsMatch;

                                        if (!allValid) {
                                            return;
                                        }

                                        const payload = {
                                            newPassword: newPassword ?? "",
                                            oldPassword: oldPassword ?? "",
                                        };

                                        updatePassword(payload, {
                                            onSuccess: () => {
                                                setNewPassword("");
                                                setConfirmPassword("");
                                                setShowConfirmPassword(false);
                                                setShowNewPassword(false);
                                                setIsEditingPassword(false);
                                                setPasswordValid({
                                                    minLength: false,
                                                    hasUppercase: false,
                                                    hasLowercase: false,
                                                    hasSpecialChar: false,
                                                    hasNumber: false,
                                                    passwordsMatch: false,
                                                });
                                                setTimeout(() => {
                                                    handleLogout();
                                                }, 1000);
                                                setAddModal(false);
                                            },
                                        });
                                    }}
                                    sx={{
                                        backgroundColor: "#546A89",
                                        color: "white",
                                        borderRadius: "12px",
                                        textTransform: "none",
                                        px: 2,
                                        py: 1,

                                        "&.Mui-disabled": {
                                            backgroundColor: "#C3CEDD",
                                            color: "#6D87AA",
                                        },
                                    }}
                                    disabled={!isRequiredProfileComplete || isEditing || updatePasswordPending}
                                >
                                    Save Changes
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
                </Box>{/* end edit-profile-login-section */}

                {/* Two Factor Section*/}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 5,
                        pl: 2,
                        mt: 2,
                    }}
                >
                    <Box sx={{textAlign: "left", flexGrow: 1, mb: 3}}>
                        <Typography fontSize="14px" fontWeight={400} color="#60768E" marginTop={2}>
                            {" "}
                            Enable 2FA Authentication
                        </Typography>
                        <Typography fontSize="14px" fontWeight={400} color="#123257">
                            {" "}
                            Add an extra layer of security by enabling Two-Factor Authentication (2FA), requiring a
                            verification code alongside your password at login.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            mr: 2.5,
                        }}
                    >
                        <Controller
                            name="twoFactorEnabled"
                            control={control}
                            render={({field}) => (
                                <Switch
                                    id="two-factor-switch"
                                    data-testid="two-factor-switch"
                                    checked={field.value}
                                    onChange={(e, checked) => {
                                        field.onChange(checked);
                                        handleSwitchChange(e, checked);
                                    }}
                                    sx={{
                                        width: "50px",
                                        height: "24px",
                                        padding: "0px",
                                        margin: "0px",
                                        "& .MuiSwitch-switchBase": {
                                            padding: "3px",
                                            "&.Mui-checked": {
                                                color: "red",
                                                transform: "translateX(26px) !important",

                                                "& + .MuiSwitch-track": {
                                                    backgroundColor: "#FF5F47",
                                                },
                                                "& .MuiSwitch-thumb": {
                                                    backgroundSize: "12px 12px",
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition: "center",
                                                    backgroundColor: "white",
                                                },
                                            },

                                            "&.Mui-disabled + .MuiSwitch-track": {
                                                opacity: "0.5 !important",
                                            },

                                            "&.Mui-unchecked": {
                                                color: "#C3CEDD",
                                                "& + .MuiSwitch-track": {
                                                    backgroundColor: "#C3CEDD",
                                                },
                                            },
                                        },
                                        "& .MuiSwitch-thumb": {
                                            color: "white",
                                            width: "18px",
                                            height: "18px",
                                            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.3)",
                                        },

                                        "& .MuiSwitch-track": {
                                            borderRadius: "20px",
                                            backgroundColor: "#C3CEDD",
                                            opacity: "1 !important",
                                            position: "relative",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>

                {/* Authenticator (TOTP) MFA — feature/totp-mfa */}
                <AuthenticatorSetup />

                {openCropDialog && (
                    <ImageCropDialog
                        open={openCropDialog}
                        onClose={() => setOpenCropDialog(false)}
                        originalFileName={originalFileName}
                        imageUrl={previewUrl2}
                        onSave={(croppedFile) => {
                            const newPreviewUrl = URL.createObjectURL(croppedFile);
                            setPreviewUrl(newPreviewUrl);
                            setOpenCropDialog(false);
                            setValue("photoUrl", croppedFile);
                            const fullData = getValues();
                            updateUser.mutate(fullData, {
                                onSuccess: () => {
                                    getMyProfile.refetch();
                                },
                                onError: () => {},
                            });
                        }}
                    />
                )}

                <PasswordConfirmDialog />

                <SuccessModal open={openModal} onClose={() => setOpenModal(false)} onContinue={handleContinue} />
                {walkthroughUI}
            </Box>
        </LocalizationProvider>
    );
};

const Field = ({label, value, required = false}: {label: string; value: string; required?: boolean}) => (
    <Box>
        <Typography fontSize="14px" fontWeight={400} color="#60768E">
            {label}
            {required && (
                <Typography component="span" sx={{color: "error.main", ml: 0.5}}>
                    *
                </Typography>
            )}
        </Typography>

        <Typography fontWeight={500} fontSize="16px" color="#123257">
            {value}
        </Typography>
    </Box>
);

const getLabelFromOptions = (options: {label: string; value: number}[], value: number | string | undefined): string => {
    const match = options.find((opt) => Number(opt.value) === Number(value));
    return match ? match.label : "N/A";
};

const getCivilStatusLabel = (value: CivilStatus | undefined): string => {
    const option = civilStatusOptions.find((opt) => opt.value === value);
    return option?.label ?? "N/A";
};

export default EditProfile;
